import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const KakaoAuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState("");
  const [signatureRequested, setSignatureRequested] = useState(false);
  const [receiptID, setReceiptID] = useState(null);
  const [signStatus, setSignStatus] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState("");

  // 스크롤 방지 설정
  useEffect(() => {
    // 페이지 진입 시 스크롤 상단으로 초기화
    window.scrollTo(0, 0);

    // 스크롤 방지 코드 제거
    return () => {
      // 클린업 함수 유지
    };
  }, []);

  // 페이지 최상단으로 스크롤 이동
  useEffect(() => {
    // 스크롤 초기화 즉시 실행
    window.scrollTo(0, 0);
    localStorage.removeItem("kakaoAuthState");
    // ElectronicSignature2.js에서 온 경우에 특별히 처리
    const resetScroll = sessionStorage.getItem("resetScroll");
    if (resetScroll === "true") {
      // setTimeout을 사용하여 DOM이 완전히 렌더링된 후 스크롤 위치 강제 설정
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }, 100);

      // 플래그 제거
      sessionStorage.removeItem("resetScroll");
    }
  }, []);

  // 페이지 로드 시 실행
  useEffect(() => {
    window.scrollTo(0, 0);

    // 저장된 인증된 전화번호 가져오기
    const phoneNumber = Cookies.get("verifiedPhoneNumber");
    if (phoneNumber) {
      setVerifiedPhone(phoneNumber);
    }

    // 로컬 스토리지에서 인증 상태 복원
    const storedAuthState = localStorage.getItem("kakaoAuthState");
    if (storedAuthState) {
      const authState = JSON.parse(storedAuthState);
      setIsAuthenticating(authState.isAuthenticating);
      setSignatureRequested(authState.signatureRequested);
      setReceiptID(authState.receiptID);

      // 이미 인증 요청이 진행 중이었다면 상태 확인 시작
      if (authState.signatureRequested && authState.receiptID) {
        startStatusPolling(authState.receiptID);
      }
    }

    // URL 파라미터 체크 (카카오에서 돌아온 경우 확인)
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("status") || searchParams.has("code")) {
      // 로컬 스토리지에서 receiptID 확인
      const storedAuthState = localStorage.getItem("kakaoAuthState");
      if (storedAuthState) {
        const authState = JSON.parse(storedAuthState);
        if (authState.receiptID) {
          startStatusPolling(authState.receiptID);
        }
      }
    }

    // 페이지 가시성 변경 및 창 포커스 이벤트 리스너 설정
    const handleVisibilityChange = () => {
      // 페이지가 다시 보이게 되면 폴링 재시작
      if (document.visibilityState === "visible") {
        const storedAuthState = localStorage.getItem("kakaoAuthState");
        if (storedAuthState) {
          const parsedState = JSON.parse(storedAuthState);

          if (parsedState.pollingStarted && parsedState.receiptId) {
            // 기존 폴링을 중지하고 새로 시작
            stopStatusPolling();
            startStatusPolling(parsedState.receiptId);
          }
        }
      }
    };

    // 창 포커스 처리 함수
    const handleWindowFocus = () => {
      const storedAuthState = localStorage.getItem("kakaoAuthState");
      if (storedAuthState) {
        const parsedState = JSON.parse(storedAuthState);

        if (parsedState.pollingStarted && parsedState.receiptId) {
          // 기존 폴링을 중지하고 새로 시작
          stopStatusPolling();
          startStatusPolling(parsedState.receiptId);
        }
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    // 컴포넌트 언마운트 시 이벤트 리스너 및 폴링 정리
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
      stopStatusPolling();
    };
  }, []);

  // 상태 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem(
      "kakaoAuthState",
      JSON.stringify({
        isAuthenticating,
        signatureRequested,
        receiptId: receiptID,
        pollingStarted: pollingIntervalRef.current !== null,
      })
    );
  }, [isAuthenticating, signatureRequested, receiptID]);

  // 서명 상태 폴링 시작
  const startStatusPolling = (receiptId) => {
    // 기존 폴링이 있으면 정지
    stopStatusPolling();

    // 상태 설정
    setIsAuthenticating(true);
    setSignatureRequested(true);

    // 로컬 스토리지에 폴링 시작 상태 저장
    localStorage.setItem(
      "kakaoAuthState",
      JSON.stringify({
        isAuthenticating: true,
        signatureRequested: true,
        receiptId: receiptId,
        pollingStarted: true,
      })
    );

    // 1초마다 서명 상태 확인
    pollingIntervalRef.current = setInterval(() => {
      checkSignatureStatus(receiptId);
    }, 1000);

    // 첫 번째 확인 즉시 실행
    checkSignatureStatus(receiptId);
  };

  // 서명 상태 폴링 중지
  const stopStatusPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // 서명 상태 확인
  const checkSignatureStatus = async (receiptId) => {
    try {
      // 토큰 가져오기
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // 애플리케이션 번호 가져오기
      const applicationNumber = Cookies.get("application_number");
      if (!applicationNumber) {
        throw new Error("신청번호가 없습니다. 이전 단계를 다시 진행해주세요.");
      }

      // 서명 상태 확인 API 호출
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/applications/sign-status/${applicationNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // 인증 오류 발생 시 폴링 중지하고 에러 표시
          stopStatusPolling();
          setAuthError("인증이 만료되었습니다. 다시 시도해주세요.");
          setShowErrorModal(true);
          return;
        }
        return; // 다른 오류는 계속 폴링
      }

      const data = await response.json();
      setSignStatus(data);

      // 상태에 따른 처리
      if (data.status === "VERIFIED") {
        // 폴링 중지
        stopStatusPolling();
        // 로컬 스토리지 정리
        localStorage.removeItem("kakaoAuthState");
        // 다음 페이지로 이동 - 결제 방법 선택 페이지로 변경
        navigate("/payment-methods");
        // navigate("/payment");
      } else if (data.status === "EXPIRED") {
        // 폴링 중지
        stopStatusPolling();
        // 오류 메시지 표시 - 카카오 인증 정보와 입력한 정보가 불일치한다는 안내
        setAuthError(
          "카카오톡에 등록된 이름/생년월일과 입력한 정보가 일치하지 않습니다. 회원정보를 다시 확인해주세요."
        );
        setShowErrorModal(true);
        setIsAuthenticating(false);
        setSignatureRequested(false);
      } else if (data.status === "PENDING") {
        // 계속 대기 상태로 폴링 유지
      } else {
        // 알 수 없는 상태에 대한 처리
      }
    } catch (error) {
      // 심각한 오류 발생 시에만 폴링 중지
      if (error.message && error.message.includes("인증 토큰")) {
        stopStatusPolling();
        setAuthError(error.message);
        setShowErrorModal(true);
      }
      // 네트워크 오류 등의 일시적인 문제는 폴링 유지
    }
  };

  const handleUserDataRedirect = () => {
    setShowErrorModal(false);
    navigate("/user-data?redirect=kakao-auth");
  };

  const handleKakaoAuth = async () => {
    setIsAuthenticating(true);
    setAuthError("");
    setSignatureRequested(true);

    try {
      // 토큰 가져오기
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);

      if (!token) {
        throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
      }

      // applicationNumber 가져오기
      const applicationNumber = Cookies.get("application_number");

      if (!applicationNumber) {
        throw new Error("신청번호가 없습니다. 이전 단계를 다시 진행해주세요.");
      }

      // API 요청 본문 구성
      const requestBody = {
        hasAgreedToElectronicProxy: true,
        hasAgreedToLegalRepresentative: true,
        hasAgreedToSuccessFee: true,
        hasAgreedToPrivacyCollection: true,
        hasAgreedToThirdPartyProvision: true,
        baseFee: parseInt(Cookies.get("lawsuit_amount") || "5000", 10),
        contingencyFeePercentage: parseInt(
          Cookies.get("lawsuit_success_fee_percentage") || "10",
          10
        ),
      };

      try {
        // API 호출
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/applications/request-sign/${applicationNumber}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error("카카오 인증 요청에 실패했습니다.");
        }

        // 성공 시 응답 데이터 처리
        const responseData = await response.json();

        // 로컬 스토리지에 상태 업데이트
        localStorage.setItem(
          "kakaoAuthState",
          JSON.stringify({
            isAuthenticating: true,
            signatureRequested: true,
            receiptId: applicationNumber,
            pollingStarted: true,
          })
        );

        // 즉시 상태 확인 폴링 시작
        stopStatusPolling();
        startStatusPolling(applicationNumber);

        // 카카오톡으로 이동할 URL이 있으면 그 URL로 이동
        if (responseData.signUrl) {
          // 현재 창에서 열기
          window.location.href = responseData.signUrl;
        }
      } catch (fetchError) {
        throw fetchError; // 다른 오류는 상위 catch로 전달
      }
    } catch (error) {
      setAuthError(error.message || "카카오 인증 중 오류가 발생했습니다.");

      // 오류 발생 시 모달 표시
      setShowErrorModal(true);

      // 폴링 중지
      stopStatusPolling();
      setIsAuthenticating(false);
      setSignatureRequested(false);
    }
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const modalVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          {!isAuthenticating && (
            <IconContainer>
              <AuthIcon src="/png/kakaoAuthIcon.png" alt="인증 아이콘" />
            </IconContainer>
          )}
          <TitleContainer>
            {!isAuthenticating && (
              <AuthTitle
                src="/png/kakaoAuthTitle.png"
                alt="계약서 전문 확인 동의가 완료되었습니다."
              />
            )}
            {isAuthenticating ? (
              <IndicatorContainer>
                <LoadingContainer>
                  <Spinner />
                </LoadingContainer>
                <AuthMessage>
                  {signatureRequested ? (
                    <>
                      카카오톡 앱에서 전자서명을 완료해주세요.
                      <br />
                      서명 후 이 페이지로 돌아오면 자동으로 진행됩니다.
                      <br />
                      <SmallText>
                        페이지를 닫지 마세요. 자동으로 확인 중입니다.
                      </SmallText>
                    </>
                  ) : (
                    <>
                      잠시만 기다려주세요. <br />
                      카카오톡으로 인증 메시지가 발송되었습니다.
                    </>
                  )}
                </AuthMessage>
              </IndicatorContainer>
            ) : (
              <>
                <StatusText></StatusText>
                {authError && <ErrorMessage>{authError}</ErrorMessage>}
              </>
            )}
          </TitleContainer>

          <ButtonContainer>
            {!isAuthenticating && (
              <KakaoButton
                src="/png/kakaoButton.png"
                alt="카카오 인증으로 동의하기"
                onClick={handleKakaoAuth}
              />
            )}
            {/* {isAuthenticating && signatureRequested && (
              <RetryButton onClick={handleKakaoAuth}>
                전자서명 다시 요청하기
              </RetryButton>
            )} */}
          </ButtonContainer>
        </Content>
      </Container>

      {/* 에러 모달 */}
      <AnimatePresence>
        {showErrorModal && (
          <ModalOverlay>
            <ErrorModal
              variants={modalVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ModalHeader>
                <ModalTitle>카카오톡 인증 오류</ModalTitle>
              </ModalHeader>
              <ModalContent>
                <WarningIcon>⚠️</WarningIcon>
                <ErrorText>
                  {authError || "카카오톡 인증에 실패했습니다."}
                </ErrorText>
                {verifiedPhone && (
                  <PhoneNumberBox>
                    <strong>현재 등록된 번호:</strong> {verifiedPhone}
                  </PhoneNumberBox>
                )}
                <InfoText>
                  카카오톡 전자서명을 위해서는 카카오에 등록된 정보와 회원정보가
                  일치해야 합니다.
                  <br />
                  회원정보를 수정한 후 다시 시도해주세요.
                </InfoText>
              </ModalContent>
              <ModalFooter>
                <ConfirmButton onClick={handleUserDataRedirect}>
                  회원정보 수정하기
                </ConfirmButton>
              </ModalFooter>
            </ErrorModal>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f8f8f8;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;

  @media (min-width: 769px) {
    padding: 20px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 768px) {
    background-color: #ffffff;
    border-radius: 20px;
    height: 700px;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  width: 100%;
  height: 100%;
  position: relative;

  @media (max-width: 768px) {
    padding: 40px 20px;
    height: 100vh;
    justify-content: space-between;
  }

  @media (min-width: 769px) {
    height: 100%;
    padding: 60px 20px;
    justify-content: space-between;
  }
`;

const IconContainer = styled.div`
  margin-top: 110px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const AuthIcon = styled.img`
  width: 160px;
  height: auto;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const AuthTitle = styled.img`
  width: 80%;
  max-width: 300px;
  height: auto;
  margin-bottom: -80px;
`;

const StatusText = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: #666666;
  margin: 0;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 80px;
  @media (min-width: 768px) {
    margin-bottom: 0px;
  }
`;

const KakaoButton = styled.img`
  width: 90%;
  max-width: 350px;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
`;
const IndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  margin-top: 150px;
`;
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 235, 0, 0.2);
  border-radius: 50%;
  border-top-color: #ffeb00;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AuthMessage = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: #333333;
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;

// 추가 스타일
const ErrorMessage = styled.p`
  font-size: 14px;
  color: #ff4c4c;
  margin-top: 15px;
  text-align: center;
  padding: 0 20px;
`;

const SmallText = styled.span`
  font-size: 12px;
  color: #666;
  display: block;
  margin-top: 10px;
`;

const RetryButton = styled.button`
  background-color: #f2f2f2;
  color: #333;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  margin-top: 15px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e5e5e5;
  }
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ErrorModal = styled(motion.div)`
  width: 90%;
  max-width: 340px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #eee;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ModalContent = styled.div`
  padding: 20px;

  p {
    font-size: 14px;
    line-height: 1.5;
    color: #666;
    margin-bottom: 16px;
  }
`;

const PhoneNumberBox = styled.div`
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
  margin: 12px 0;
  font-size: 14px;

  strong {
    color: #333;
  }
`;

const ModalFooter = styled.div`
  padding: 0 20px 20px;
`;

const ConfirmButton = styled.button`
  width: 100%;
  height: 48px;
  background-color: #ffeb00;
  color: #3c1e1e;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

const WarningIcon = styled.div`
  font-size: 36px;
  margin-bottom: 10px;
  text-align: center;
`;

const ErrorText = styled.p`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #333;
  margin-bottom: 16px;
  text-align: center;
`;

const InfoText = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: #666;
  margin-top: 16px;
  text-align: center;
  background-color: #f8f8f8;
  padding: 12px;
  border-radius: 8px;
`;

export default KakaoAuthPage;
